import { Box, ButtonPropsSizeOverrides, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { OverridableStringUnion } from '@mui/types';
import { AnalyticsContext, OrderApprovalStatusType, selectUserPermissionSetting, useAppSelector } from 'common';
import { useState } from 'react';
import { CfContainedButton } from '../../../../../cf-ui/buttons/CfContainedButton';
import { DeviceType, useBreakpoint } from '../../../../../hooks/useBreakpoint';
import RejectOrderDialog from '../../RejectOrderDialog/RejectOrderDialog';
import { OrderSummaryContinueButton } from './OrderSummaryContinueButton';
import { OrderSummaryReviewButton } from './OrderSummaryReviewButton';
import { OrderSummarySubmitButton } from './OrderSummarySubmitButton';

interface OrderSummaryActionButtonsProps {
  mode: 'entry' | 'review';
  size?: OverridableStringUnion<'small' | 'medium' | 'large', ButtonPropsSizeOverrides>;
  showContinueShoppingButton?: boolean;
  showPrice?: boolean;
  analyticsContext?: AnalyticsContext;
  onNavigateToOrderEntry?: () => void;
  onNavigateToOrderReview?: () => void;
  onNavigateToOrderConfirmation?: () => void;
  onRejectOrder?: () => void;
  sx?: SxProps<Theme>;
  orderEntryHeaderId?: string;
  orderApprovalStatusType?: OrderApprovalStatusType;
  orderUserId?: string;
}

export const OrderSummaryActionButtons = (props: OrderSummaryActionButtonsProps): JSX.Element => {
  const canSubmitOrder = useAppSelector(selectUserPermissionSetting)?.CanSubmitOrder ?? false;
  const userId = useAppSelector((s) => s.customer.selectedCustomer?.UserId) ?? '';
  const showRejectButton =
    canSubmitOrder &&
    props.orderApprovalStatusType != null &&
    props.orderApprovalStatusType !== OrderApprovalStatusType.None &&
    userId !== props.orderUserId;
  const { deviceType } = useBreakpoint();
  const [rejectDialog, setRejectDialog] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...props.sx,
        }}
      >
        {props.showContinueShoppingButton && (
          <OrderSummaryContinueButton size={props.size} onNavigateToOrderEntry={props.onNavigateToOrderEntry} />
        )}
        {props.mode === 'entry' && (
          <OrderSummaryReviewButton
            size={props.size}
            showPrice={props.showPrice}
            onNavigateToOrderReview={props.onNavigateToOrderReview}
          />
        )}
        {props.mode === 'review' && (
          <>
            {showRejectButton && (
              <CfContainedButton
                color='secondary'
                size={props.size}
                isFullWidth={deviceType !== DeviceType.Tablet}
                onClick={() => {
                  setRejectDialog(true);
                  props.onRejectOrder?.();
                }}
                isDisabled={props.orderApprovalStatusType === OrderApprovalStatusType.Rejected}
              >
                Reject order
              </CfContainedButton>
            )}
            <OrderSummarySubmitButton
              size={props.size}
              onNavigateToOrderConfirmation={props.onNavigateToOrderConfirmation}
              analyticsContext={props.analyticsContext}
            />
          </>
        )}
      </Box>
      {rejectDialog && (
        <RejectOrderDialog orderEntryHeaderId={props.orderEntryHeaderId ?? ''} onClose={() => setRejectDialog(false)} />
      )}
    </>
  );
};
